import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { HelpIcon, HelpIconContainer } from '../helpIconContainer';
import { Position } from '../ztooltip';
import ValidationMessage from '../validation/validationMessage';
import { useValidationControl, ValidationControlProps } from 'src/shared/validation';

export type RadioVariant = 'default' | 'ai';

const RADIO_LABEL_GAP = '5px';

const StyledDefault = styled.div<{ $fullWidth?: boolean; disabled?: boolean; $variant?: RadioVariant; $isError?: boolean }>`
    font-family: ${(props) => props.theme.app.font};
    text-align: left;
    border: none;
    display: ${(props) => (props.$fullWidth ? 'flex' : 'inline-flex')};
    width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
    transition: background-color ease 0.2s;
    cursor: pointer;
    position: relative;
    margin: 0;
    padding: 0;
    align-items: flex-start;
    min-height: 20px;
    flex-direction: column;
    border-radius: 4px;
    ${(props) => props.$isError && 'box-shadow: 0 0 0 2px rgba(222, 28, 34, 0.2);'}

    label {
        font-size: ${(props) => props.theme.app.component.radio.textSize};
        font-weight: ${(props) => props.theme.app.component.radio.textWeight};
        color: #263238;
        margin-right: 10px;
        padding-left: calc(${(props) => props.theme.app.component.radio.width} + ${RADIO_LABEL_GAP}) !important;
    }

    input[type='radio']:checked,
    input[type='radio']:not(:checked) {
        display: none;
    }

    input[type='radio']:checked + label,
    input[type='radio']:not(:checked) + label {
        position: relative;
        padding-left: calc(${(props) => props.theme.app.component.radio.width} + ${RADIO_LABEL_GAP});
        cursor: pointer;
        line-height: 20px;
        min-height: 20px;
        display: inline-flex;
        font-size: ${(props) => props.theme.app.component.radio.textSize};
        font-weight: ${(props) => props.theme.app.component.radio.textWeight};
    }

    input[type='radio']:checked + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 2px;
        width: ${(props) => props.theme.app.component.radio.width};
        height: ${(props) => props.theme.app.component.radio.height};
        box-sizing: border-box;
        border: ${(props) =>
            props.$variant === 'ai'
                ? `2px solid ${props.theme.ai.solid}`
                : props.disabled
                  ? props.theme.app.component.radio.selected.disabled.border
                  : props.theme.app.component.radio.selected.active.border};
        border-radius: ${(props) => props.theme.app.component.radio.borderRadius};
        background: ${(props) => props.theme.app.component.radio.selected.active.background};
    }

    input[type='radio']:not(:checked) + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 2px;
        width: ${(props) => props.theme.app.component.radio.width};
        height: ${(props) => props.theme.app.component.radio.height};
        box-sizing: border-box;
        border: ${(props) =>
            props.disabled ? props.theme.app.component.radio.unselected.disabled.border : props.theme.app.component.radio.unselected.active.border};
        border-radius: ${(props) => props.theme.app.component.radio.borderRadius};
        background: ${(props) =>
            props.disabled ? props.theme.app.component.radio.unselected.disabled.background : props.theme.app.component.radio.unselected.active.background};
    }

    input[type='radio']:checked + label:after,
    input[type='radio']:not(:checked) + label:after {
        content: '';
        width: 10px;
        height: 10px;
        background: ${(props) =>
            props.$variant === 'ai'
                ? props.theme.ai.gradient
                : props.disabled
                  ? props.theme.app.component.radio.selected.disabled.inputColor
                  : props.theme.app.component.radio.selected.active.inputColor};
        position: absolute;
        left: 3px;
        top: 5px;
        border-radius: 50%;
        -webkit-transition: all 0.2s ease;
        transition: all 0.2s ease;
        border: none;
    }

    input[type='radio']:not(:checked) + label:after {
        opacity: 0;
        transform: scale(0);
    }

    input[type='radio']:checked + label:after {
        opacity: 1;
        transform: scale(1);
    }
`;

const RadioLabelContainer = styled.div`
    padding-top: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: ${(props) => props.theme.app.component.radio.textSize} !important;
    font-weight: ${(props) => props.theme.app.component.radio.textWeight} !important;
`;

const RadioCaption = styled.span`
    display: inline-block;
    font-size: 12px;
    line-height: 1.4;
    color: #78909c;
`;

const CaptionContainer = styled.div`
    display: flex;
    margin-left: calc(${(props) => props.theme.app.component.radio.width} + ${RADIO_LABEL_GAP});
    margin-top: 4px;
`;

const ParentContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

const BadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    > *:first-child {
        margin-left: 20px;
    }
`;

const StyledHelpIconContainer = styled(HelpIconContainer)`
    margin-left: 5px;
`;

interface RadioProps<T extends number | string> extends ValidationControlProps<any> {
    id?: string;
    name: string;
    label: string | React.ReactNode;
    caption?: React.ReactNode;
    value: T;
    onSelect?: (value: T) => void;
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    checked?: boolean;
    className?: string;
    disabled?: boolean;
    tooltip?: string;
    tooltipPosition?: Position;
    variant?: RadioVariant;
    badges?: React.ReactNode[];
}

export const Radio = <T extends number | string>(props: PropsWithChildren<RadioProps<T>>) => {
    const validation = useValidationControl<HTMLDivElement, any>(props, props.checked);
    const handleChange = () => {
        if (props.onSelect) props.onSelect(props.value);
    };

    return (
        <>
            <StyledDefault
                ref={validation.setRef}
                className={props.className}
                disabled={props.disabled}
                $variant={props.variant}
                $isError={validation.isValidationError}
            >
                <ParentContainer>
                    <input
                        id={props.id}
                        disabled={props.disabled}
                        type='radio'
                        name={props.name}
                        value={props.value}
                        checked={props.checked}
                        onChange={handleChange}
                    />
                    <label htmlFor={props.id} onClick={() => !props.disabled && handleChange()}>
                        <RadioLabelContainer>{props.label}</RadioLabelContainer>
                    </label>
                    {props.tooltip && (
                        <StyledHelpIconContainer tooltip={props.tooltip} position={props.tooltipPosition}>
                            <HelpIcon />
                        </StyledHelpIconContainer>
                    )}

                    {props.badges && props.badges.length > 0 && (
                        <BadgeContainer>
                            {props.badges.map((badge, index) => (
                                <React.Fragment key={index}>{badge}</React.Fragment>
                            ))}
                        </BadgeContainer>
                    )}
                </ParentContainer>

                {props.caption && (
                    <CaptionContainer>
                        <RadioCaption>{props.caption}</RadioCaption>
                    </CaptionContainer>
                )}
                {validation.validationKey && <ValidationMessage validationKey={validation.validationKey} deferUntilSubmit />}
            </StyledDefault>
        </>
    );
};
